import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SEO from './SEO';
import '../styles/ThankYouPage.css';

const ThankYouPage = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [showFireworks, setShowFireworks] = useState(false);

  useEffect(() => {
    // Iniciar animación de fuegos artificiales después de un breve delay
    const timer = setTimeout(() => {
      setShowFireworks(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const openWhatsApp = () => {
    const phoneNumber = '5491123428342';
    const message = encodeURIComponent('¡Hola! Me acabo de suscribir a Runamatic y me gustaría que me ayuden a gestionar mi cuenta. ¡Gracias!');
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const seoData = {
    title: 'Gracias por tu suscripción - Runamatic',
    description: 'Gracias por suscribirte a Runamatic. Te contactaremos pronto para configurar tu cuenta de automatización.',
    keywords: 'gracias, suscripción, runamatic, automatización, whatsapp',
    canonical: '/gracias',
    schema: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Página de Agradecimiento",
      "description": "Página de confirmación de suscripción a Runamatic",
      "url": "https://runamatic.com/gracias"
    }
  };

  return (
    <>
      <SEO {...seoData} />
      <section className="thankyou-section gradient-hero" ref={ref}>
        {/* Animación de fuegos artificiales */}
        {showFireworks && (
          <div className="fireworks-container">
            <div className="firework firework-1">
              <div className="explosion explosion-1"></div>
              <div className="explosion explosion-2"></div>
              <div className="explosion explosion-3"></div>
            </div>
            <div className="firework firework-2">
              <div className="explosion explosion-1"></div>
              <div className="explosion explosion-2"></div>
              <div className="explosion explosion-3"></div>
            </div>
            <div className="firework firework-3">
              <div className="explosion explosion-1"></div>
              <div className="explosion explosion-2"></div>
              <div className="explosion explosion-3"></div>
            </div>
          </div>
        )}

        {/* Background shapes */}
        <div className="thankyou-background">
          <div className="thankyou-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
            <div className="shape shape-4"></div>
          </div>
        </div>

        <div className="container">
          <div className="thankyou-content">
            <motion.div 
              className="thankyou-main"
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {/* Ícono de éxito */}
              <motion.div 
                className="success-icon"
                initial={{ scale: 0, opacity: 0 }}
                animate={inView ? { scale: 1, opacity: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.5, type: "spring", stiffness: 150 }}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="#4CAF50"/>
                  <path d="M8 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </motion.div>

              {/* Título principal */}
              <motion.h1 
                className="thankyou-title"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.7 }}
              >
                ¡Gracias por elegir{' '}
                <span className="text-gradient">Runamatic</span>!
              </motion.h1>

              {/* Descripción */}
              <motion.p 
                className="thankyou-description"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.9 }}
              >
                Tu suscripción ha sido confirmada exitosamente. Estamos emocionados de tenerte como parte de la familia Runamatic y ayudarte a revolucionar tu atención al cliente.
              </motion.p>

              {/* Mensaje importante */}
              <motion.div 
                className="next-steps"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.1 }}
              >
                <div className="next-steps-header">
                  <h3>Próximos pasos:</h3>
                </div>
                <p>
                  Para comenzar a configurar tu cuenta y aprovechar al máximo todas las funcionalidades de Runamatic, 
                  por favor contáctanos a través de WhatsApp. Nuestro equipo te ayudará a configurar tu cuenta 
                  y te guiará en los primeros pasos.
                </p>
              </motion.div>

              {/* Call to action principal */}
              <motion.div 
                className="thankyou-cta"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.3 }}
              >
                <motion.button
                  className="btn btn-primary btn-large whatsapp-btn"
                  onClick={openWhatsApp}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg className="whatsapp-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  Escribir por WhatsApp
                </motion.button>
              </motion.div>

              {/* Información adicional */}
              <motion.div 
                className="additional-info"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.5 }}
              >
                <div className="info-grid">
                  <div className="info-item">
                    <div className="info-icon">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="info-text">
                      <h4>Respuesta rápida</h4>
                      <p>Te contactaremos en menos de 24 horas</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <div className="info-icon">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
                        <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2"/>
                        <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2"/>
                        <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2"/>
                        <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                    <div className="info-text">
                      <h4>Configuración personalizada</h4>
                      <p>Adaptamos la plataforma a tus necesidades</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <div className="info-icon">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                    <div className="info-text">
                      <h4>Soporte dedicado</h4>
                      <p>Equipo experto para acompañarte</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ThankYouPage; 